import { MinusCircleOutlined, PlusCircleOutlined } from "@ant-design/icons"
import { Button, Col, Dropdown, Form, FormInstance, Input, InputNumber, Menu, Modal, Row, Space, TimePicker } from "antd"
import { decodeJWT } from "common/functions"
import { emailToUsername } from "common/utils"
import i18n from "internationalization/i18n"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useCreatePlanMutation, useGetPlanDetailsQuery, useSavePlanMutation } from "services/pricingService"
import './PlanModal.less'
import { rootReducerType } from "redux/combineReducers";
import moment from "moment"
import { createPlanParams, savePlanParams } from "services/utils/interfaces"
import Notification from "common/components/NotificationMessage/Notification"

const unitLabel = {
  h: '/hour',
  kWh: '/kWh',
  min: '/min'
}

const dollarItems = ['maxChargePerDay', 'minimumCharge', 'maxChargePerDay', 'parkingRate', 'idleRate', 'defaultRate']

const intervalLimit = 30

const getTime = (time: moment.Moment) => {
  return time.hours() * 60 + time.minutes()
}

const RateUnitLabel: React.FC<{
  select?: boolean
  unit: string
  setUnit: (arg: any) => void
}> = ({ select = false, unit, setUnit }) => {
  const SelectUnit = () => {
    return (
      <Menu>
        <Menu.Item key={'kWh'} onClick={() => setUnit('kWh')}>
          $/kWh
        </Menu.Item>
        <Menu.Item key={'min'} onClick={() => setUnit('min')}>
          $/min
        </Menu.Item>
      </Menu>
    )
  }
  return (
    <div style={{ display: 'flex' }}>
      {
        select ? <Dropdown overlay={SelectUnit} trigger={['click']}>
          <div style={{
            cursor: 'pointer',
            color: '#fbcd3d'
          }}>{unitLabel[unit]}</div>
        </Dropdown> : <>{unitLabel[unit]}</>
      }
    </div>
  )
}

const TimeHorizon: React.FC<{
  times: any[],
  enable: boolean
  defaultRate: number
}> = ({ times, enable, defaultRate }) => {
  const total = 1440
  const Slice: React.FC<{
    hourRange: any[],
    rate: number,
  }> = ({ hourRange, rate }) => {
    const getLeft = (moment: any) => {
      let count = moment.hours() * 60 + moment.minute()
      return count / total * 100
    }
    const getWidth = (momentS: any, momentE: any) => {
      let start = momentS.hours() * 60 + momentS.minute()
      start = start / total * 100
      let end = momentE.hours() * 60 + momentE.minute()
      end = end / total * 100
      return end - start
    }
    return (
      <div className="time-slice" style={{
        left: `${getLeft(hourRange[0])}%`,
        width: `${getWidth(hourRange[0], hourRange[1])}%`
      }}>
        <div className="node start">
        </div>
        <p>${rate || 0}</p>
        <div className="node end">
        </div>
      </div>
    )
  }
  const marks: React.ReactNode[] = []
  for (let i = 0; i <= 24; i++) {
    marks.push(<span key={i} className="mark" style={{
      left: `${i * 60 / total * 100}%`
    }}>{i}</span>)
  }

  const getSlices = () => {
    let flag = moment('0001-01-01 00:00')
    let sliceComputed: any[] = []
    times.map((item) => {
      if (getTime(item.hourRange[0]) > getTime(flag)) {
        sliceComputed.push({
          hourRange: [flag, item.hourRange[0]],
          rate: defaultRate
        })
      }
      flag = item.hourRange[1]
      sliceComputed.push(item)
    })
    if (getTime(flag) <= 1440) {
      sliceComputed.push({
        hourRange: [flag, moment('0001-01-01 23:59')],
        rate: defaultRate
      })
    }
    return sliceComputed
  }
  return (
    <>
      {
        enable ?
          <div className="time-horizon">
            {
              getSlices().map((item) => <Slice key={item.hourRange} hourRange={item.hourRange} rate={item.rate} />)
            }
            <div className="mark-line">
              {marks}
            </div>
          </div> :
          <div style={{ color: 'red' }}>Please set the time period correctly</div>
      }
    </>
  )
}

const verifySchedule = (form: FormInstance<any>) => {
  let error = false
  let tempSchedules = [...form.getFieldsValue().schedules as Array<any>]
  tempSchedules.sort((last, next) => {
    if (!last.hourRange || !next.hourRange) {
      return 0
    }
    return getTime(last.hourRange[0]) - getTime(next.hourRange[0])
  })
  let flag = 0
  tempSchedules.forEach((item) => {
    if (item.hourRange && getTime(item.hourRange[0]) >= flag) {
      flag = getTime(item.hourRange[1])
    } else {
      error = true
    }
  })
  return error ? 'error' : [...tempSchedules]
}

export const CreateModal: React.FC<{
  planName?: string
  refetch: any
  onCancel: () => void
}> = ({ refetch, onCancel }) => {
  const [form] = Form.useForm();
  const [slice, setSlice] = useState<any>([])
  const [curType, setCurType] = useState('plug_in_time_based')
  const [maxActive, setMaxActive] = useState({
    rate: false,
    time: false,
  })
  const [unit, setUnit] = useState('h')
  const [createPlan, { isSuccess, data }] = useCreatePlanMutation();
  const [defaultRate, setDefaultRate] = useState(0)
  useEffect(() => {
    if (isSuccess) {
      Notification({
        type: data?.success ? "success" : "warning",
        message: data?.message || '',
        description: ''
      });
      refetch()
      onCancel()
    }
  }, [isSuccess]);

  const PlanTabs: React.FC = () => {
    const switchType = (type) => {
      if (['energy_based', 'DCFC'].includes(type)) {
        setUnit('kWh')
      } else {
        setUnit('h')
      }
      setCurType(type)
    }
    const PlanTab: React.FC<{
      type: string
      label: string
    }> = ({ type, label }) => {
      return <Button style={curType === type ? {
        color: '#4d75d1',
        pointerEvents: 'none',
        borderColor: '#4d75d1',
        fontWeight: 'bold',
      } : {}}
        onClick={() => switchType(type)}
      >
        {label}
      </Button>
    }
    return (
      <Space style={{ marginRight: 12, width: 0, flex: 1 }}
        wrap={true}
      >
        <PlanTab
          type={'plug_in_time_based'}
          label={"Plug-in time-based"}
        />
        <PlanTab
          type={'charging_time_based'}
          label={"Charging time-based"}
        />
        <PlanTab
          type={'energy_based'}
          label={"Energy-based"}
        />
        <PlanTab
          type={'DCFC'}
          label={"DCFC"}
        />
      </Space>
    )
  }



  const Schedule: React.FC = () => {
    return (
      <>
        <Form.Item label="Time horizon">
          <TimeHorizon times={slice} enable={slice !== 'error'} defaultRate={defaultRate} />
        </Form.Item>
        <Form.Item label={curType === 'plug_in_time_based' ? 'Hourly rate' : 'Charging rate'} name="defaultRate" rules={[{ required: true, message: "" }]}>
          <InputNumber onChange={(value) => setDefaultRate(value)}
            min={0} addonBefore="$" addonAfter={<RateUnitLabel select={curType === 'DCFC'} unit={unit} setUnit={setUnit} />} style={{ width: 150 }} />
        </Form.Item>
        <Form.List name="schedules">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline" wrap={true}>
                  <Form.Item
                    label={curType === 'plug_in_time_based' ? 'Hourly rate' : 'Charging rate'}
                    name={[name, 'rate']}
                    rules={[{ required: true, message: "" }]}
                  >
                    <InputNumber min={0} addonBefore="$" addonAfter={unitLabel[unit]} style={{ width: 150 }}
                      onChange={() => setSlice(verifySchedule(form))}
                    />
                  </Form.Item>
                  <Form.Item
                    label={'Schedule'}
                    name={[name, 'hourRange']}
                    rules={[{ required: true, message: "" }]}
                  >
                    <TimePicker.RangePicker format={'HH:mm'} onChange={(value) => {
                      if (value) {
                        let gap = getTime(value[1] as any) - getTime(value[0] as any)
                        if (gap < intervalLimit) {
                          Notification({
                            type: "warning",
                            message: `The time interval should be at least ${intervalLimit} minutes or more`,
                            description: ''
                          });
                          setSlice('error')
                          return false
                        }
                      }
                      setSlice(verifySchedule(form))
                    }} />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => {
                    remove(name)
                    setSlice(verifySchedule(form))
                  }} />
                </Space>
              ))}
              {fields.length < 4 && <PlusCircleOutlined onClick={add} />}
            </>
          )}
        </Form.List>
      </>

    )
  }

  return (
    <Modal
      title={i18n.t("Create Pricing Plan")}
      visible={true}
      onCancel={onCancel}
      footer={null}
      width={1024}
    >
      {/* <div style={{ display: "flex", marginBottom: 24 }}>
        <PlanTabs />
      </div> */}
      <Form
        form={form}
      >
        <Form.Item label="Plan name" name="pricingPlanName" rules={[{ required: true, message: "" }]}>
          <Input style={{ width: 300 }} />
        </Form.Item>
        <Schedule />
        <Row>
          <Col sm={24} md={12}>
            <Form.Item label="Minimum charge per session" name="minimumCharge" rules={[{ required: true, message: "" }]}>
              <InputNumber min={0} addonBefore="$" style={{ width: 150 }} />
            </Form.Item>
          </Col>
          {
            ['plug_in_time_based', 'charging_time_based'].includes(curType) &&
            <>
              <Col sm={24} md={12}>
                <Form.Item label="Maximum charge per day" name="maxChargePerDay" rules={[{ required: maxActive.rate || maxActive.time, message: "" }]}>
                  <InputNumber min={0} addonBefore="$" style={{ width: 150 }} onChange={(value) => {
                    let old = { ...maxActive }
                    old.rate = value !== null
                    setMaxActive(old)
                  }} />
                </Form.Item>
              </Col>
              <Col sm={24} md={12}>
                <Form.Item label="Maximum charge cutoff time" name="maxChargeCutoff" rules={[{ required: maxActive.rate || maxActive.time, message: "" }]}>
                  <TimePicker format={'HH:mm'} onChange={(value) => {
                    let old = { ...maxActive }
                    old.time = value !== null
                    setMaxActive(old)
                  }} />
                </Form.Item>
              </Col>
            </>
          }
          {
            ['energy_based', 'charging_time_based', 'DCFC'].includes(curType) &&
            <>
              <Col sm={24} md={12}>
                <Form.Item label="Parking rate" name="parkingRate" initialValue={0} rules={[{ required: true, message: "" }]}>
                  <InputNumber min={0} addonBefore="$" addonAfter="/hour" style={{ width: 150 }} />
                </Form.Item>
              </Col>
              <Col sm={24} md={12}>
                <Form.Item label="Idle fee" name="idleRate" initialValue={0} rules={[{ required: true, message: "" }]}>
                  <InputNumber min={0} addonBefore="$" addonAfter="$/hour" style={{ width: 150 }} />
                </Form.Item>
              </Col>
            </>
          }
        </Row>
      </Form>
      <div className="model-btn-buttons">
        <Button style={{
          marginLeft: 'auto',
          background: '#ccc',
          color: '#fff',
          borderColor: '#ccc',
          fontWeight: 'bold',
          width: '48%'
        }}
          onClick={() => {
            onCancel()
          }}>
          Cancel
        </Button>
        <Button style={{
          background: '#9dcc78',
          color: '#fff',
          borderColor: '#9dcc78',
          fontWeight: 'bold',
          width: '48%'
        }}
          onClick={() => {
            if (slice === 'error') {
              return false
            }
            form.validateFields().then(() => {
              const values = form.getFieldsValue()
              let pricingSchedules: any[] = []
              slice.map((item: any) => {
                pricingSchedules.push({ hourRange: `${item.hourRange[0].format('HH:mm')}-${item.hourRange[1].format('HH:mm')}`, rate: item.rate * 100 })
              })
              let params = {
                pricingPlanName: values.pricingPlanName,
                pricingPlanType: curType,
                unit: unit,
                pricingSchedules: pricingSchedules
              } as createPlanParams
              if (values.maxChargeCutoff) {
                params.maxChargeCutoff = values.maxChargeCutoff.format('HH:mm')
              }
              dollarItems.map((item) => {
                if (values[item]) {
                  params[item] = values[item] * 100
                }
              })
              createPlan(params)
            })
              .catch((err) => false)
          }}
        >Save</Button>
      </div>
    </Modal>
  )
}

export const EditModal: React.FC<{
  planName: string
  refetch: any
  onCancel: () => void
}> = ({ planName, refetch, onCancel }) => {
  const { isLoggedIn } = useSelector(
    (state: rootReducerType) => state.authenticationReducer
  );
  const [form] = Form.useForm();
  const [slice, setSlice] = useState<any>([])
  const [curType, setCurType] = useState('plug_in_time_based')
  const [maxActive, setMaxActive] = useState({
    rate: false,
    time: false,
  })
  const [savePlan, { isSuccess, data: saveDataRes }] = useSavePlanMutation()
  const [initial, setInitial] = useState<any>(false)
  const [unit, setUnit] = useState('h')
  const [defaultRate, setDefaultRate] = useState(0)
  const { data, refetch: getRefetch } = useGetPlanDetailsQuery({
    userName: emailToUsername(decodeJWT(isLoggedIn).email),
    planName: planName
  })
  useEffect(() => {
    if (isSuccess) {
      Notification({
        type: saveDataRes?.success ? "success" : "warning",
        message: saveDataRes?.message || '',
        description: ''
      });
      getRefetch()
      refetch()
      onCancel()
    }
  }, [isSuccess]);

  useEffect(() => {
    if (data) {
      let initial = { ...data }
      setCurType(initial.pricingPlanType)
      if (data.maxChargeCutoff) {
        initial.maxChargeCutoff = moment(`0001-01-01 ${initial.maxChargeCutoff}`)
      }
      let pricingSchedules: any = []
      initial.pricingSchedules.map((slice) => {
        if(!slice.hourRange) {
          return slice
        }
        let hourRange = slice.hourRange
        let hours = hourRange.split('-')
        pricingSchedules.push({
          rate: slice.rate / 100,
          hourRange: [moment(`0001-01-01 ${hours[0]}`), moment(`0001-01-01 ${hours[1]}`)]
        })
      })
      dollarItems.map((item) => {
        if (data[item]) {
          initial[item] = data[item] / 100
        }
      })
      initial.schedules = pricingSchedules
      setSlice(pricingSchedules)
      setUnit(initial.unit)
      setInitial(initial)
      setDefaultRate(initial.defaultRate)
    }
  }, [data])


  const Schedule: React.FC = () => {
    return (
      <>
        <Form.Item label="Time horizon">
          <TimeHorizon times={slice} enable={slice !== 'error'} defaultRate={defaultRate} />
        </Form.Item>
        <Form.Item label={curType === 'plug_in_time_based' ? 'Hourly rate' : 'Charging rate'} name="defaultRate" rules={[{ required: true, message: "" }]}>
          <InputNumber onChange={(value) => setDefaultRate(value)}
            min={0} addonBefore="$" addonAfter={<RateUnitLabel select={curType === 'DCFC'} unit={unit} setUnit={setUnit} />} style={{ width: 150 }} />
        </Form.Item>
        <Form.List name="schedules">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline" wrap={true}>
                  <Form.Item
                    label={curType === 'plug_in_time_based' ? 'Hourly rate' : 'Charging rate'}
                    name={[name, 'rate']}
                    rules={[{ required: true, message: "" }]}
                  >
                    <InputNumber min={0} addonBefore="$" addonAfter={unitLabel[unit]} style={{ width: 150 }}
                      onChange={() => setSlice(verifySchedule(form))}
                    />
                  </Form.Item>
                  <Form.Item
                    label={'Schedule'}
                    name={[name, 'hourRange']}
                    rules={[{ required: true, message: "" }]}
                  >
                    <TimePicker.RangePicker format={'HH:mm'} onChange={(value) => {
                      if (value) {
                        let gap = getTime(value[1] as any) - getTime(value[0] as any)
                        if (gap < intervalLimit) {
                          Notification({
                            type: "warning",
                            message: `The time interval should be at least ${intervalLimit} minutes or more`,
                            description: ''
                          });
                          setSlice('error')
                          return false
                        }
                      }
                      setSlice(verifySchedule(form))
                    }} />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => {
                    remove(name)
                    setSlice(verifySchedule(form))
                  }} />
                </Space>
              ))}
              {fields.length < 4 && <PlusCircleOutlined onClick={add} />}
            </>
          )}
        </Form.List>
      </>

    )
  }

  return (
    <Modal
      title={i18n.t("Create Pricing Plan")}
      visible={true}
      onCancel={onCancel}
      footer={null}
      width={1024}
    >
      {
        initial &&
        <>
          {/* <div style={{ display: "flex", marginBottom: 24 }}>
            <Button style={{
              color: '#4d75d1',
              pointerEvents: 'none',
              borderColor: '#4d75d1',
              fontWeight: 'bold',
            }}
            >
              {curType}
            </Button>
          </div> */}
          <Form
            form={form}
            initialValues={initial}
          >
            <Form.Item label="Plan name" name="pricingPlanName" rules={[{ required: true, message: "" }]}>
              <Input style={{ width: 300 }} />
            </Form.Item>
            <Schedule />
            <Row>
              <Col sm={24} md={12}>
                <Form.Item label="Minimum charge per session" name="minimumCharge" rules={[{ required: true, message: "" }]}>
                  <InputNumber min={0} addonBefore="$" style={{ width: 150 }} />
                </Form.Item>
              </Col>
              {
                ['plug_in_time_based', 'charging_time_based'].includes(curType) &&
                <>
                  <Col sm={24} md={12}>
                    <Form.Item label="Maximum charge per day" name="maxChargePerDay" rules={[{ required: maxActive.rate || maxActive.time, message: "" }]}>
                      <InputNumber min={0} addonBefore="$" style={{ width: 150 }} onChange={(value) => {
                        let old = { ...maxActive }
                        old.rate = value !== null
                        setMaxActive(old)
                      }} />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item label="Maximum charge cutoff time" name="maxChargeCutoff" rules={[{ required: maxActive.rate || maxActive.time, message: "" }]}>
                      <TimePicker format={'HH:mm'} onChange={(value) => {
                        let old = { ...maxActive }
                        old.time = value !== null
                        setMaxActive(old)
                      }} />
                    </Form.Item>
                  </Col>
                </>
              }
              {
                ['energy_based', 'charging_time_based', 'DCFC'].includes(curType) &&
                <>
                  <Col sm={24} md={12}>
                    <Form.Item label="Parking rate" name="parkingRate" rules={[{ required: true, message: "" }]}>
                      <InputNumber min={0} addonBefore="$" addonAfter="/hour" style={{ width: 150 }} />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item label="Idle fee" name="idleRate" rules={[{ required: true, message: "" }]}>
                      <InputNumber min={0} addonBefore="$" addonAfter="/hour" style={{ width: 150 }} />
                    </Form.Item>
                  </Col>
                </>
              }
            </Row>
          </Form>
          <div className="model-btn-buttons">
            <Button style={{
              marginLeft: 'auto',
              background: '#ccc',
              color: '#fff',
              borderColor: '#ccc',
              fontWeight: 'bold',
              width: '48%'
            }}
              onClick={() => {
                onCancel()
              }}>
              Cancel
            </Button>
            <Button style={{
              marginLeft: 'auto',
              background: '#9dcc78',
              color: '#fff',
              borderColor: '#9dcc78',
              fontWeight: 'bold',
              width: '48%'
            }}
              onClick={() => {
                if (slice === 'error') {
                  return false
                }
                form.validateFields().then(() => {
                  const values = form.getFieldsValue()
                  let pricingSchedules: any[] = []
                  slice.map((item: any) => {
                    pricingSchedules.push({ hourRange: `${item.hourRange[0].format('HH:mm')}-${item.hourRange[1].format('HH:mm')}`, rate: item.rate * 100 })
                  })
                  let params = {
                    pricingPlanOldName: planName,
                    pricingPlanNewName: values.pricingPlanName,
                    pricingPlanType: curType,
                    unit: unit,
                    pricingSchedules: pricingSchedules
                  } as savePlanParams
                  if (values.maxChargeCutoff) {
                    params.maxChargeCutoff = values.maxChargeCutoff.format('HH:mm')
                  }
                  dollarItems.map((item) => {
                    if (values[item]) {
                      params[item] = values[item] * 100
                    }
                  })
                  savePlan(params)
                })
                  .catch(() => false)
              }}
            >Save</Button>
          </div>
        </>
      }
    </Modal>
  )
}